import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AdminUsageReport.css';
import './AdminShareLogs.css';

const THAI_MONTHS = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const THAI_MONTHS_FULL = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

const CHANNEL_META = {
    line: { label: 'LINE', color: '#06C755' },
    facebook: { label: 'Facebook', color: '#1877F2' },
    instagram: { label: 'Instagram', color: '#E1306C' },
    tiktok: { label: 'TikTok', color: '#111827' },
    save: { label: 'บันทึก/เมนูแชร์', color: '#0ea5e9' },
    download: { label: 'ดาวน์โหลด', color: '#64748b' },
};
const FIELD_LABELS = {
    avatar: 'รูปโปรไฟล์', name: 'ชื่อ', position: 'ตำแหน่ง', dept: 'หน่วยงาน', account: 'เลขบัญชี',
    net: 'ยอดเด่น', totals: 'รวมรับ/หัก', tax: 'ภาษี', gpf: 'กบข.',
    incomeList: 'รายการรับ', deductionList: 'รายการหัก', monthlyChart: 'กราฟรายเดือน',
};
const HERO_LABELS = { net: 'รับสุทธิ', gross: 'รายได้รวม', both: 'ทั้งสองยอด' };

function fmt(n) { return new Intl.NumberFormat('th-TH').format(n || 0); }
function thaiDate(dateStr) {
    if (!dateStr) return '-';
    const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
    return `${d} ${THAI_MONTHS[m]} ${y + 543}`;
}
// created_at ถูกเก็บเป็นเวลาไทย wall-clock แล้ว ไม่ต้องแปลง timezone
function thaiDateTime(s) {
    if (!s) return '-';
    const str = String(s).replace('T', ' ').replace('Z', '').split('.')[0];
    const [d, t] = str.split(' ');
    return `${thaiDate(d)} ${t ? t.slice(0, 5) : ''}`;
}
function todayStr() {
    const t = new Date(Date.now() + 7 * 60 * 60 * 1000);
    return t.toISOString().slice(0, 10);
}
function daysAgoStr(n) {
    const t = new Date(Date.now() + 7 * 60 * 60 * 1000 - n * 86400000);
    return t.toISOString().slice(0, 10);
}

function ChannelBadge({ channel }) {
    const m = CHANNEL_META[channel] || { label: channel, color: '#94a3b8' };
    return <span className="share-chip" style={{ '--chip': m.color }}>{m.label}</span>;
}

function AdminShareLogs() {
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({ from_date: daysAgoStr(29), to_date: todayStr(), channel: 'all', search: '' });
    const [applied, setApplied] = useState(filters);
    const [page, setPage] = useState(1);

    const fetchStats = useCallback(async () => {
        try {
            const res = await api.get('/reports/share-log/stats', { params: { days } });
            setStats(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'โหลดสถิติไม่สำเร็จ');
        }
    }, [days]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/reports/share-log', { params: { ...applied, page, limit: 50 } });
            setLogs(res.data.logs || []);
            setPagination(res.data.pagination || { page: 1, totalPages: 1, total: 0 });
        } catch (err) {
            setError(err.response?.data?.error || 'โหลดประวัติไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    }, [applied, page]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const submitFilters = (e) => {
        e.preventDefault();
        setPage(1);
        setApplied(filters);
    };

    const s = stats?.summary || {};
    const priv = stats?.privacy || {};
    const totalRange = s.range_count || 0;
    const maxChannel = Math.max(1, ...(stats?.by_channel || []).map(c => c.cnt));

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>ประวัติการแชร์สลิป</h1>
                <p>ใครแชร์สลิปเดือนใด ผ่านช่องทางไหน และเลือกแสดงข้อมูลอะไรบ้าง — รวมในรายงานประจำวัน 18:00 น. ด้วย</p>
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

            {/* สรุป */}
            <div className="usage-cards">
                <div className="usage-card share">
                    <span className="usage-card-value">{fmt(s.today_count)}</span>
                    <span className="usage-card-label">แชร์วันนี้</span>
                    <span className="usage-card-sub">{fmt(s.today_users)} คน</span>
                </div>
                <div className="usage-card users">
                    <span className="usage-card-value">{fmt(s.range_count)}</span>
                    <span className="usage-card-label">แชร์ใน {days} วัน</span>
                    <span className="usage-card-sub">{fmt(s.range_users)} คน</span>
                </div>
                <div className="usage-card tawi">
                    <span className="usage-card-value">{fmt(priv.with_account)}</span>
                    <span className="usage-card-label">แสดงเลขบัญชี</span>
                    <span className="usage-card-sub">ครั้ง ใน {days} วัน</span>
                </div>
                <div className="usage-card slip">
                    <span className="usage-card-value">{fmt(priv.masked)}</span>
                    <span className="usage-card-label">ปิดบังตัวเลข</span>
                    <span className="usage-card-sub">ครั้ง ใน {days} วัน</span>
                </div>
                <div className="usage-card email">
                    <span className="usage-card-value">{fmt(priv.with_details)}</span>
                    <span className="usage-card-label">แสดงรายการแยก</span>
                    <span className="usage-card-sub">ครั้ง ใน {days} วัน</span>
                </div>
            </div>

            <div className="usage-controls">
                <div className="usage-range">
                    <label>ช่วงสถิติ:</label>
                    {[7, 30, 90].map(d => (
                        <button key={d} className={`range-btn ${days === d ? 'active' : ''}`} onClick={() => setDays(d)}>{d} วัน</button>
                    ))}
                </div>
            </div>

            <div className="share-stats-grid">
                {/* แยกช่องทาง */}
                <div className="steam-card">
                    <div className="steam-card-header">แยกตามช่องทาง ({days} วัน)</div>
                    <div className="steam-card-body">
                        {(stats?.by_channel || []).length === 0 ? (
                            <p className="share-empty">ยังไม่มีการแชร์</p>
                        ) : stats.by_channel.map(c => {
                            const m = CHANNEL_META[c.channel] || { label: c.channel, color: '#94a3b8' };
                            return (
                                <div className="share-bar-row" key={c.channel}>
                                    <span className="share-bar-label"><ChannelBadge channel={c.channel} /></span>
                                    <div className="share-bar-track">
                                        <div className="share-bar-fill" style={{ width: `${(c.cnt / maxChannel) * 100}%`, background: m.color }} />
                                    </div>
                                    <span className="share-bar-val">{fmt(c.cnt)} <small>({totalRange ? Math.round(c.cnt / totalRange * 100) : 0}%)</small></span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ผู้แชร์บ่อย */}
                <div className="steam-card">
                    <div className="steam-card-header">แชร์บ่อยสุด ({days} วัน)</div>
                    <div className="steam-card-body" style={{ padding: 0 }}>
                        <table className="usage-table">
                            <thead><tr><th>ชื่อ</th><th className="num">ครั้ง</th><th>ล่าสุด</th></tr></thead>
                            <tbody>
                                {(stats?.top_users || []).length === 0 ? (
                                    <tr><td colSpan="3" className="share-empty">ยังไม่มีข้อมูล</td></tr>
                                ) : stats.top_users.map(u => (
                                    <tr key={u.user_id}>
                                        <td>{u.fullname || u.username}</td>
                                        <td className="num">{fmt(u.cnt)}</td>
                                        <td className="share-muted">{thaiDateTime(u.last_share)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* รายวัน */}
            <div className="steam-card" style={{ marginBottom: 18 }}>
                <div className="steam-card-header">สรุปรายวัน ({days} วัน)</div>
                <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    <table className="usage-table">
                        <thead><tr><th>วันที่</th><th className="num">แชร์ (ครั้ง)</th><th className="num">ผู้แชร์ (คน)</th></tr></thead>
                        <tbody>
                            {(stats?.daily || []).length === 0 ? (
                                <tr><td colSpan="3" className="share-empty">ยังไม่มีข้อมูล</td></tr>
                            ) : stats.daily.map(r => (
                                <tr key={r.date}>
                                    <td>{thaiDate(r.date)}</td>
                                    <td className="num">{fmt(r.cnt)}</td>
                                    <td className="num">{fmt(r.users)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ตัวกรอง */}
            <form className="share-filters" onSubmit={submitFilters}>
                <label>จาก <input type="date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} /></label>
                <label>ถึง <input type="date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} /></label>
                <label>ช่องทาง
                    <select value={filters.channel} onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}>
                        <option value="all">ทั้งหมด</option>
                        {Object.entries(CHANNEL_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                    </select>
                </label>
                <label>ค้นหา <input type="text" placeholder="ชื่อ / CID" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} /></label>
                <button type="submit" className="steam-btn steam-btn-primary">ค้นหา</button>
            </form>

            {/* รายการ */}
            <div className="steam-card">
                <div className="steam-card-header">
                    <span>รายการแชร์</span>
                    <span className="badge badge-primary">{fmt(pagination.total)} รายการ</span>
                </div>
                <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: 40 }}><div className="spinner"></div></div>
                    ) : (
                        <table className="usage-table share-log-table">
                            <thead>
                                <tr>
                                    <th>เวลา</th>
                                    <th>ผู้แชร์</th>
                                    <th>สลิปเดือน</th>
                                    <th>ช่องทาง</th>
                                    <th>ยอดเด่น</th>
                                    <th>ข้อมูลที่แสดง</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr><td colSpan="7" className="share-empty">ไม่พบรายการ</td></tr>
                                ) : logs.map(l => {
                                    const fields = String(l.shown_fields || '').split(',').filter(Boolean);
                                    return (
                                        <tr key={l.id}>
                                            <td className="share-muted nowrap">{thaiDateTime(l.created_at)}</td>
                                            <td>
                                                <div>{l.fullname || '-'}</div>
                                                <div className="share-muted">{l.username}</div>
                                            </td>
                                            <td className="nowrap">{l.salary_month === 0 ? 'สรุปทั้งปี' : THAI_MONTHS_FULL[l.salary_month]} {l.salary_year + 543}</td>
                                            <td><ChannelBadge channel={l.channel} /></td>
                                            <td className="nowrap">{HERO_LABELS[l.hero_mode] || l.hero_mode || '-'}</td>
                                            <td>
                                                <div className="share-fields">
                                                    {fields.map(f => (
                                                        <span key={f} className={`share-field ${f === 'account' ? 'warn' : ''}`}>{FIELD_LABELS[f] || f}</span>
                                                    ))}
                                                    {l.mask_amounts ? <span className="share-field mask">ปิดบังตัวเลข</span> : null}
                                                    {fields.length === 0 && !l.mask_amounts ? <span className="share-muted">-</span> : null}
                                                </div>
                                            </td>
                                            <td className="share-muted nowrap">{l.ip_address || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                {pagination.totalPages > 1 && (
                    <div className="share-pagination">
                        <button className="range-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ ก่อนหน้า</button>
                        <span>หน้า {pagination.page} / {pagination.totalPages}</span>
                        <button className="range-btn" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>ถัดไป ›</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminShareLogs;
